import Popover from "common/Popover/Popover";
import NotificationsList from "features/notifications/NotificationsList";
import Title from "common/Typography/Title";
import styled from "styled-components";
import { zIndexes } from "@shared/common/Themes/AppTheme";
import Link from "common/Typography/Link";
import useMedia from "utils/hooks/useMedia";
import { ReactNode, useContext } from "react";
import { AvoContext } from "analytics/AvoProvider";
import SettingOutlined from "@ant-design/icons/SettingOutlined";
import { useHistory } from "react-router-dom";
import {
  MOBILE_SEARCH,
  NOTIFICATIONS_SETTINGS_PATH,
  SMALL_MOBILE,
} from "common/constants";
import IconActionButton from "common/Buttons/IconActionButton";
import Flex from "common/Boxes/Flex";
import { sharedLib } from "@shared/services/shared.service";
import Modal from "common/Modal/Modal";
import useMeasure from "utils/hooks/useMeasure";

interface NotificationsModalProps {
  trigger: ReactNode;
  onCloseModal: () => void;
  onOpenModal: () => void;
  numUnreadNotifications: number;
  visible: boolean;
}

const NotificationsModal = ({
  trigger,
  onCloseModal,
  onOpenModal,
  numUnreadNotifications,
  visible,
}: NotificationsModalProps) => {
  const history = useHistory();
  const { avo } = useContext(AvoContext);
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const isSmallMobile = useMedia(
    [`(max-width: ${SMALL_MOBILE})`],
    [true],
    false
  );
  const [measureTriggerRef, triggerSize] = useMeasure<HTMLDivElement>();
  const [markNotificationsRead] =
    sharedLib.sharedApi.notifications.useMarkNotificationsReadMutation();

  const onMarkNotificationsRead = () => {
    markNotificationsRead();
    avo?.notificationMarkAllAsRead({
      numberOfUnreadNotifications: numUnreadNotifications,
    });
  };

  const onSettingsIconClick = () => history.push(NOTIFICATIONS_SETTINGS_PATH);

  const content = (
    <ContentWrapper onClick={(e) => e.preventDefault()}>
      <TitleBox>
        <Flex>
          <Title level={3} style={{ marginRight: 8 }}>
            Notifications
          </Title>
          <IconActionButton
            icon={<SettingOutlined />}
            actionName="Settings"
            onClick={onSettingsIconClick}
            secondaryButton={true}
          />
        </Flex>
        <Link onClick={onMarkNotificationsRead}>Mark all as read</Link>
      </TitleBox>
      <NotificationsList closeModal={onCloseModal} />
    </ContentWrapper>
  );

  const handleVisibleChange = (visible: boolean) => {
    if (visible) onOpenModal();
    else onCloseModal();
  };

  if (isMobile) {
    return (
      <>
        <div
          onClick={() => handleVisibleChange(!visible)}
          ref={measureTriggerRef}
        >
          {trigger}
        </div>
        <Modal
          open={visible}
          onCancel={onCloseModal}
          styles={{
            body: {
              maxHeight: 575,
              overflow: "auto",
              padding: 0,
            },
          }}
          style={{ top: triggerSize.bottom }}
          closeIcon={null}
        >
          {content}
        </Modal>
      </>
    );
  }

  return (
    <StyledPopover
      content={content}
      trigger={["click"]}
      open={visible}
      onOpenChange={handleVisibleChange}
      placement={isMobile ? undefined : "bottomRight"}
      overlayClassName={`notification-modal-popover${
        isMobile ? " hide-arrow" : ""
      }`}
      zIndex={zIndexes.popover}
      styles={{
        root: {
          width: isSmallMobile ? "calc(100% - 24px)" : 400,
          maxWidth: "100%",
        },
        body: {
          padding: 0,
        },
      }}
      arrow={{ pointAtCenter: true }}
    >
      {trigger}
    </StyledPopover>
  );
};

export default NotificationsModal;

const ContentWrapper = styled.div`
  width: 100%;
`;
const TitleBox = styled.div`
  padding: 20px 16px;
  border-bottom: ${(p) => p.theme.borders.lightest};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const StyledPopover = styled(Popover)`
  &.ant-popover {
    left: unset;
    inset: unset;
    right: 16px;
  }
`;
